import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import PersonCard from './PersonCard';
import { peopleFeed } from '../api/social';
import { useCategories } from '../api/categories';
import { LANGUAGES } from '../lib/creatorMeta';

const PAGE = 20;

/**
 * Phase F1 · Voice-First People Feed — replaces the classic post feed
 * when the admin flag `people_feed_enabled` is ON. Simple filters only
 * (Voce Pro / Live / language), neutral stable ordering from the backend.
 */
export default function PeopleFeed() {
  const { t } = useTranslation('home');
  const [people, setPeople] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all'); // all | voce_pro | live | favorites
  const [language, setLanguage] = useState('');
  // Phase F5.2 — ONE macro-category filter (server-side, before pagination).
  const [category, setCategory] = useState('');
  const { categories } = useCategories();

  const buildParams = useCallback((skipVal) => ({
    limit: PAGE,
    skip: skipVal,
    ...(filter === 'voce_pro' ? { voce_pro: true } : {}),
    ...(filter === 'live' ? { live: true } : {}),
    ...(filter === 'favorites' ? { favorites: true } : {}),
    ...(language ? { language } : {}),
    ...(category ? { category } : {}),
  }), [filter, language, category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await peopleFeed(buildParams(0));
      setPeople(res.people || []);
      setHasMore(!!res.has_more);
      setSkip(res.skip_next || PAGE);
    } catch (_e) {
      setPeople([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await peopleFeed(buildParams(skip));
      setPeople((prev) => {
        const seen = new Set(prev.map((x) => x.user_id));
        return [...prev, ...(res.people || []).filter((x) => !seen.has(x.user_id))];
      });
      setHasMore(!!res.has_more);
      setSkip(res.skip_next || skip + PAGE);
    } catch (_e) { /* silent */ } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const chips = [
    { key: 'all', label: t('people.filter_all') },
    { key: 'favorites', label: t('people.filter_favorites') },
    { key: 'voce_pro', label: t('people.filter_voce_pro') },
    { key: 'live', label: t('people.filter_live') },
  ];

  return (
    <div className="mx-auto w-full max-w-[560px] px-3 pb-24 pt-4 md:px-0 md:pb-8" data-testid="people-feed">
      <header className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400 gold-text">
          <Users className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">{t('people.title')}</h1>
          <p className="text-xs text-muted-foreground">{t('people.subtitle')}</p>
        </div>
      </header>

      <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="people-filters">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === c.key
                ? 'gold-gradient text-black'
                : 'border border-border bg-card text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`people-filter-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Phase F5.2.1 — category as dropdown (matches language selector).
          Backend filter unchanged: category=<slug> applied BEFORE pagination,
          LIVE → ONLINE → OFFLINE ordering preserved. */}
      <div className="mb-4 flex items-center gap-2" data-testid="people-selects">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="min-w-0 flex-1 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground"
          data-testid="people-filter-category"
        >
          <option value="">{t('people.cat_all')}</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="min-w-0 flex-1 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground"
          data-testid="people-filter-language"
        >
          <option value="">{t('people.any_lang')}</option>
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" data-testid="people-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('people.loading')}
        </div>
      )}

      {!loading && people.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground" data-testid="people-empty">
          {t('people.empty')}
        </div>
      )}

      {!loading && people.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="people-list">
          {people.map((p) => <PersonCard key={p.user_id} p={p} />)}
        </div>
      )}

      {!loading && hasMore && (
        <div className="mt-5 flex justify-center">
          <Button
            variant="secondary"
            className="rounded-full px-6 text-sm"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="people-load-more"
          >
            {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('people.load_more')}
          </Button>
        </div>
      )}
    </div>
  );
}
